import copy from "copy-to-clipboard";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";
import {
  formatDate,
  formatDateToISODateString,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import { useDebounce } from "../../../util/useDebounce";
import {
  IEmployee,
  IEmployeeWithProjects,
  IProject,
  IProjectWithTasks,
  ITask,
  ITaskSheet,
  ITaskSheetResponse,
} from "./Tasksheet.props";

const dateFilterOptions = [
  { id: "1", name: "All" },
  { id: "2", name: "Today" },
  { id: "3", name: "Tomorrow" },
  { id: "4", name: "Yesterday" },
  { id: "8", name: "Current Week" },
  { id: "10", name: "Next Week" },
  { id: "9", name: "Last Week" },
  { id: "5", name: "Last 7 Days" },
  { id: "6", name: "Last Month" },
  { id: "7", name: "Custom" },
];

const statusFilterOptions = [
  { id: "", name: "All Statuses" },
  { id: "todo", name: "To Do" },
  { id: "in progress", name: "In Progress" },
  { id: "query", name: "Query" },
  { id: "finished", name: "Finished" },
  { id: "backLog", name: "Back Log" },
];

const useTaskSheet = () => {
  const [selectedTaskSheetId, setSelectedTaskSheetId] = useState<string | null>(
    null
  );
  const limit = 50;
  const [taskSheetData, setTaskSheetData] = useState<IEmployeeWithProjects[]>(
    []
  );
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectStatus, setSelectStatus] = useState<string>("");
  const [user, setUser] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [project, setProject] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [selectedDateFilter, setSelectedDateFilter] = useState("2"); // Default to "Today"
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [searchValue, setSearchValue] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(""); // Add status filter state

  // Custom date modal states
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customFromDate, setCustomFromDate] = useState<Date | null>(null);
  const [customToDate, setCustomToDate] = useState<Date | null>(null);
  
  // Copy data modal states
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  let fromDate: Date | null = null;
  let toDate: Date | null = null;
  const navigate = useNavigate();

  // Use debounced search
  const debouncedSetSearch = useDebounce((value: string) => {
    setSearch(value);
  }, 500);

  useEffect(() => {
    fetchData();
    fetchProjectData();
    // Fetch initial task data with default "Today" filter
    handleDateFilterChange("2");
  }, []);

  // Set default to "Today" and expand all users on initial load
  useEffect(() => {
    if (taskSheetData.length > 0 && expandedUsers.size === 0) {
      const allUserIds = taskSheetData.map((emp) => emp.employee._id);
      setExpandedUsers(new Set(allUserIds));
    }
  }, [taskSheetData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getEmployees}`,
      });
      setUser([{ id: null, name: "select User" }, ...result]);
      setUserId(null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getProjects}`,
      });
      setProject([{ id: null, name: "select Project" }, ...result]);
      setProjectId(null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = `${apiPaths.getAllTaskSheets}?page=${page}&limit=${limit}`;

      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
      }

      if (search) {
        endpoint += `&search=${search}`;
      }

      if (userId) {
        endpoint += `&assignedTo=${userId}`;
      }

      if (projectId) {
        endpoint += `&projectId=${projectId}`;
      }

      if (statusFilter) {
        endpoint += `&status=${statusFilter}`;
      }

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      // Transform the flat task structure to nested employee-project-task structure
      if (result && result.data && Array.isArray(result.data)) {
        console.log("Transforming flat task structure to nested structure");
        console.log("Raw data array length:", result.data.length);
        console.log("First task sample:", result.data[0]);

        // Group tasks by assignedTo (employee) and then by projectId
        const employeeMap = new Map<string, IEmployeeWithProjects>();

        let processedTasks = 0;
        let skippedTasks = 0;

        result.data.forEach((task: any) => {
          // Skip tasks without required fields
          if (!task.assignedTo?._id || !task.projectId?._id) {
            console.warn("Skipping task with missing required fields:", task);
            skippedTasks++;
            return;
          }

          processedTasks++;
          const employeeId = task.assignedTo._id;
          const employeeName = task.assignedTo.name || "Unknown Employee";
          const employeeEmail = task.assignedTo.email || "";

          const projectId = task.projectId._id;
          const projectName = task.projectId.name || "Unknown Project";
          const projectDescription = task.projectId.description || "";
          const projectStatus = task.projectId.status || "Active";

          // Create or get employee
          if (!employeeMap.has(employeeId)) {
            employeeMap.set(employeeId, {
              employee: {
                _id: employeeId,
                name: employeeName,
                email: employeeEmail,
              },
              projects: [],
            });
          }

          const employeeData = employeeMap.get(employeeId)!;

          // Find or create project
          let projectData = employeeData.projects.find(
            (p) => p.project._id === projectId
          );
          if (!projectData) {
            projectData = {
              project: {
                _id: projectId,
                name: projectName,
                description: projectDescription,
                status: projectStatus,
              },
              tasks: [],
            };
            employeeData.projects.push(projectData);
          }

          // Add task to project
          projectData.tasks.push({
            _id: task._id,
            description: task.description || "",
            priority: task.priority || "low",
            dueDate: task.dueDate || "",
            hours: task.hours || "0",
            videoUrl: task.videoUrl || "",
            isUserVideoReq: task.isUserVideoReq || false,
            userVideoUrl: task.userVideoUrl || null,
            status: task.status || "todo",
            createdAt: task.createdAt || "",
            updatedAt: task.updatedAt || "",
          });
        });

        console.log(
          `Processed ${processedTasks} tasks, skipped ${skippedTasks} tasks`
        );

        // Convert map to array
        const transformedData = Array.from(employeeMap.values());
        console.log("Transformed data:", transformedData);
        console.log("Total employees:", transformedData.length);
        console.log(
          "Total projects:",
          transformedData.reduce((total, emp) => total + emp.projects.length, 0)
        );
        console.log(
          "Total tasks:",
          transformedData.reduce(
            (total, emp) =>
              total +
              emp.projects.reduce(
                (projTotal, proj) => projTotal + proj.tasks.length,
                0
              ),
            0
          )
        );

        setTaskSheetData(transformedData);
        setTotalPage(result.totalPages || 1);
      } else if (result && result.data && result.data.employees) {
        // New nested structure
        setTaskSheetData(result.data.employees);
        setTotalPage(result.totalPages || 1);
      } else if (result && Array.isArray(result)) {
        // Old flat structure - transform to new structure for compatibility
        console.log("Setting data with array structure:", result);
        const transformedData: IEmployeeWithProjects[] = [];
        setTaskSheetData(transformedData);
        setTotalPage(1);
      } else if (result && result.employees) {
        // Direct employees array
        console.log("Setting data with direct employees:", result.employees);
        setTaskSheetData(result.employees);
        setTotalPage(result.totalPages || 1);
      } else {
        console.log(
          "No data structure matched, setting empty array. Result:",
          result
        );
        setTaskSheetData([]);
        setTotalPage(1);
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
      setTaskSheetData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setSelectedTaskSheetId(id);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };

  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addTaskSheetAdmin");
  };

  // Refresh data function - refetches data with current filter settings
  const refreshData = () => {
    // Get current date filter values
    const now = new Date();
    let refreshFromDate: Date | null = null;
    let refreshToDate: Date | null = null;

    switch (selectedDateFilter) {
      case "1": // All
        refreshFromDate = null;
        refreshToDate = null;
        break;
      case "2": // Today
        refreshFromDate = now;
        refreshToDate = now;
        break;
      case "3": // Tomorrow
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        refreshFromDate = tomorrow;
        refreshToDate = tomorrow;
        break;
      case "4": // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        refreshFromDate = yesterday;
        refreshToDate = yesterday;
        break;
      case "5": // Last 7 Days
        const last7Days = new Date(now);
        last7Days.setDate(last7Days.getDate() - 7);
        refreshFromDate = last7Days;
        refreshToDate = now;
        break;
      case "6": // Last Month
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        refreshFromDate = lastMonth;
        refreshToDate = now;
        break;
      case "7": // Custom
        refreshFromDate = customFromDate;
        refreshToDate = customToDate;
        break;
      case "8": // Current Week
        const dayOfWeek = now.getDay();
        const sunday = new Date(now);
        sunday.setDate(now.getDate() - dayOfWeek);
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        refreshFromDate = sunday;
        refreshToDate = saturday;
        break;
      case "9": // Last Week
        const currentSunday = new Date(now);
        currentSunday.setDate(now.getDate() - currentSunday.getDay());
        const lastWeekStart = new Date(currentSunday);
        lastWeekStart.setDate(currentSunday.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        refreshFromDate = lastWeekStart;
        refreshToDate = lastWeekEnd;
        break;
      case "10": // Next Week
        const nextWeekStart = new Date(now);
        nextWeekStart.setDate(now.getDate() - now.getDay() + 7);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        refreshFromDate = nextWeekStart;
        refreshToDate = nextWeekEnd;
        break;
      default:
        refreshFromDate = now;
        refreshToDate = now;
    }

    // Refetch data with current filters
    handleDateFilter(refreshFromDate, refreshToDate);
  };

  const updateScreen = (type: ViewType) => {
    updateComponent(type, () => {}, setSelectedTaskSheetId);
    // Refresh data after clearing the edit view
    if (type === "edit") {
      // Small delay to ensure state is updated before refetching
      setTimeout(() => {
        refreshData();
      }, 100);
    }
  };

  const handleSelectStatus = (e: any, id: any) => {
  const status = e.target.value;
  handleConfirmStatusUp(id, status);
};

const handleConfirmStatusUp = async (
  id: string,
  status:
    | "todo"
    | "in progress"
    | "query"
    | "finished"
    | "backLog"
) => {
  setLoading(true);

  try {
    await ApiService({
      method: "PUT",
      endpoint: `${apiPaths.updateTaskSheet}${id}`,
      data: { status },
    });

    // ✅ IMMEDIATE UI UPDATE
    setTaskSheetData((prevData) =>
      prevData.map((employeeData) => ({
        ...employeeData,
        projects: employeeData.projects.map((projectData) => ({
          ...projectData,
          tasks: projectData.tasks.map((task) =>
            task._id === id
              ? { ...task, status }
              : task
          ),
        })),
      }))
    );
  } catch (error) {
    console.error("Error updating status:", error);
  } finally {
    setLoading(false);
  }
};


  // Search handling
  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSetSearch(value);
  };

  // Date filter handling
  const handleDateFilterChange = (filterId: string) => {
    setSelectedDateFilter(filterId);
   
    if (filterId === "7") {
      // Custom date - open modal
      setShowCustomDateModal(true);
      return;
    }

    // Call the date filter handler with appropriate dates based on selection
    const now = new Date();

    switch (filterId) {
      case "1": // All
        fromDate = null;
        toDate = null;
        break;
      case "2": // Today
        fromDate = now;
        toDate = now;
        break;
      case "3": // Tomorrow
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        fromDate = tomorrow;
        toDate = tomorrow;
        break;
      case "4": // Yesterday
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = yesterday;
        toDate = yesterday;
        break;
      case "5": // Last 7 Days
        const last7Days = new Date(now);
        last7Days.setDate(last7Days.getDate() - 7);
        fromDate = last7Days;
        toDate = now;
        break;
      case "6": // Last Month
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        fromDate = lastMonth;
        toDate = now;
        break;
      case "8": // Current Week
        const dayOfWeek = now.getDay();
        const sunday = new Date(now);
        sunday.setDate(now.getDate() - dayOfWeek);
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        fromDate = sunday;
        toDate = saturday;
        break;
      case "9": // Last Week
        const currentSunday = new Date(now);
        currentSunday.setDate(now.getDate() - currentSunday.getDay());
        const lastWeekStart = new Date(currentSunday);
        lastWeekStart.setDate(currentSunday.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        fromDate = lastWeekStart;
        toDate = lastWeekEnd;
        break;
      case "10": // Next Week
        const nextWeekStart = new Date(now);
        nextWeekStart.setDate(now.getDate() - now.getDay() + 7);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        fromDate = nextWeekStart;
        toDate = nextWeekEnd;
        break;
      default:
        fromDate = null;
        toDate = null;
    }

    handleDateFilter(fromDate, toDate);
  };

  // Custom date modal handlers
  const handleCustomDateSubmit = () => {
    if (customFromDate && customToDate) {
      handleDateFilter(customFromDate, customToDate);
      setShowCustomDateModal(false);
    }
  };

  const handleCustomDateCancel = () => {
    setShowCustomDateModal(false);
    setCustomFromDate(null);
    setCustomToDate(null);
    // Reset to "Today" if custom date is cancelled
    setSelectedDateFilter("2");
    const today = new Date();
    handleDateFilter(today, today);
  };

  // Accordion handling
  const toggleUserAccordion = (userId: string) => {
    if (!userId) {
      return;
    }

    const newExpandedUsers = new Set(expandedUsers);
    if (newExpandedUsers.has(userId)) {
      newExpandedUsers.delete(userId);
    } else {
      newExpandedUsers.add(userId);
    }
    setExpandedUsers(newExpandedUsers);
  };

  const expandAllUsers = () => {
    const allUserIds = taskSheetData.map((emp) => emp.employee._id);
    setExpandedUsers(new Set(allUserIds));
  };

  const collapseAllUsers = () => {
    setExpandedUsers(new Set());
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchValue("");
    setSearch("");
    setUserId(null);
    setProjectId(null);
    setSelectedDateFilter("2"); // Reset to "Today"
    setStatusFilter(""); // Reset status filter
    const today = new Date();
    handleDateFilter(today, today);
    // Keep all users expanded
    const allUserIds = taskSheetData.map((emp) => emp.employee._id);
    setExpandedUsers(new Set(allUserIds));
  };

  // Calculate total tasks for each user
  const getUserTotalTasks = (employeeData: IEmployeeWithProjects) => {
    return employeeData.projects.reduce(
      (total, project) => total + project.tasks.length,
      0
    );
  };

  // Calculate total tasks across all users
  const getTotalTasks = () => {
    return taskSheetData.reduce(
      (total, employee) => total + getUserTotalTasks(employee),
      0
    );
  };

  const handleCopyAllData = () => {
    if (!taskSheetData || taskSheetData.length === 0) {
      toast.error("No data to copy!");
      return;
    }
    // Open the modal instead of directly copying
    setShowCopyModal(true);
    // Initialize with no users selected by default
    // const allUserIds = taskSheetData.map((emp) => emp.employee._id);
    setSelectedUserIds(new Set());
  };

  const toggleUserSelection = (userId: string) => {
    const newSelectedUserIds = new Set(selectedUserIds);
    if (newSelectedUserIds.has(userId)) {
      newSelectedUserIds.delete(userId);
    } else {
      newSelectedUserIds.add(userId);
    }
    setSelectedUserIds(newSelectedUserIds);
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === taskSheetData.length) {
      // If all are selected, deselect all
      setSelectedUserIds(new Set());
    } else {
      // Otherwise, select all
      const allUserIds = taskSheetData.map((emp) => emp.employee._id);
      setSelectedUserIds(new Set(allUserIds));
    }
  };

  const handleConfirmCopy = () => {
    try {
      if (selectedUserIds.size === 0) {
        toast.error("Please select at least one user!");
        return;
      }

      // Filter data based on selected users
      const filteredData = taskSheetData.filter((employeeData) =>
        selectedUserIds.has(employeeData.employee._id)
      );

      // Group tasks by assigned person
      const groupedTasks: { [key: string]: any[] } = {};

      filteredData.forEach((employeeData: IEmployeeWithProjects) => {
        const employeeName = employeeData.employee.name;
        if (!groupedTasks[employeeName]) {
          groupedTasks[employeeName] = [];
        }

        employeeData.projects.forEach((projectData: IProjectWithTasks) => {
          projectData.tasks.forEach((task: ITask) => {
            groupedTasks[employeeName].push({
              ...task,
              projectName: projectData.project.name,
            });
          });
        });
      });

      // Format grouped data
      const formattedData = Object.entries(groupedTasks)
        .map(([employeeName, tasks]) => {
          const taskLines = tasks
            .map((task: any) => {
              const projectName = task.projectName || "N/A";
              const description = task.description || "N/A";
              const priority = task.priority || "N/A";
              const dueDate = task.dueDate ? formatDate(task.dueDate) : "N/A";
              const status = task.status || "N/A";

              return ` ${projectName}  - ${description} - ${priority} - ${dueDate} - ${status}`;
            })
            .join("\n");

          return `${employeeName}\n${taskLines}`;
        })
        .join("\n\n");

      copy(formattedData);
      toast.success(`Task data for ${selectedUserIds.size} user(s) copied to clipboard!`);
      setShowCopyModal(false);
    } catch (error) {
      console.error("Error copying data:", error);
      toast.error("Error copying data to clipboard!");
    }
  };

  const handleCancelCopy = () => {
    setShowCopyModal(false);
    setSelectedUserIds(new Set());
  };
  const handleVideoClick = (videoUrl: string) => {
    try {
      if (!videoUrl) {
        console.warn("Invalid videoUrl for handleVideoClick:", videoUrl);
        return;
      }
      setSelectedVideoUrl(videoUrl);
      setShowVideoModal(true);
    } catch (error) {
      console.error("Error handling video click:", error);
    }
  };
  useEffect(() => {
    handleDateFilterChange(selectedDateFilter);
  }, [userId, projectId, search, statusFilter]);

  return {
    selectedTaskSheetId,
    taskSheetData,
    showConfirmation,
    handleAdd,
    handleEdit,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    handleSelectStatus,
    updateScreen,
    loading,
    user,
    userId,
    project,
    projectId,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
    setUserId,
    setProjectId,
    // New search and filter related
    searchValue,
    handleSearch,
    selectedDateFilter,
    handleDateFilterChange,
    // Accordion related
    expandedUsers,
    toggleUserAccordion,
    expandAllUsers,
    collapseAllUsers,
    // Custom date modal related
    showCustomDateModal,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
    handleCustomDateSubmit,
    handleCustomDateCancel,
    // Utility functions
    getUserTotalTasks,
    getTotalTasks,
    clearAllFilters,
    dateFilterOptions,
    handleCopyAllData,
    handleVideoClick,
    showVideoModal,
    setShowVideoModal,
    selectedVideoUrl,
    setSelectedVideoUrl,
    statusFilter,
    setStatusFilter,
    statusFilterOptions,
    // Copy modal related
    showCopyModal,
    selectedUserIds,
    toggleUserSelection,
    toggleSelectAll,
    handleConfirmCopy,
    handleCancelCopy,
  };
};

export default useTaskSheet;
